import {useCallback, useEffect, useRef} from "react";
import {FilePondFile} from "filepond";
import {UseFormReturn} from "react-hook-form";
import {isEmpty} from "lodash";
import {SelectOption} from "../../../common";
import {shouldUseRestrictedOptions} from "../form-components/auth-hours/ClinicalRecommendationInput";
import {getFieldsToResetForHourVisibility, getHourVisibility} from "../form-components/auth-hours/resetHiddenFields";
import {filterServiceTypesToAvailableOptions} from "../staffing-plan/useLatestOfferedHours";
import {
    AuthorizationInfo,
    ClientReportSubmitInput,
    defaultClinicalRecommendationHoursFromAuth,
    defaultServiceTypesByAuthTier1,
    isShortAddendumReport,
    ReportServiceType,
    requireServiceType,
} from "../domain";

type UseClientReportModalPrefillParams = {
    showModal: boolean;
    activeAuth: AuthorizationInfo | undefined;
    userWantsToSubmit: boolean;
    reportType: string;
    serviceTypes: ReportServiceType[];
    methods: UseFormReturn<ClientReportSubmitInput>;
    isFetchingLatestHoursData: boolean;
    restrictedOptions: Map<string, string> | null;
    defaultReportType: string;
};

export function useClientReportModalPrefill({
    showModal,
    activeAuth,
    userWantsToSubmit,
    reportType,
    serviceTypes,
    methods,
    isFetchingLatestHoursData,
    restrictedOptions,
    defaultReportType,
}: UseClientReportModalPrefillParams) {
    const {setValue, getValues} = methods;

    // Only seed the service type from the active authorization once per modal open
    // or authorization change so users can still clear it after the initial prefill.
    const hasPrefilledServiceTypeRef = useRef(false);
    useEffect(() => {
        hasPrefilledServiceTypeRef.current = false;
    }, [activeAuth?.guid, showModal]);

    // Set the default reportType when the modal opens, if one is not already chosen.
    useEffect(() => {
        if (!showModal || isEmpty(defaultReportType)) {
            return;
        }
        if (isEmpty(getValues('reportType'))) {
            setValue('reportType', defaultReportType);
        }
    }, [showModal, defaultReportType, getValues, setValue]);

    // Pre-fill the service type and clinical recommendation hour fields from the
    // active authorization whenever the selected report type or authorization changes.
    useEffect(() => {
        if (!showModal || !activeAuth || !userWantsToSubmit) {
            return;
        }
        if (!reportType) {
            return;
        }
        if (!isShortAddendumReport(reportType) && !requireServiceType(reportType)) {
            return;
        }

        const useRestrictedOptions = shouldUseRestrictedOptions(reportType, restrictedOptions);

        if (isEmpty(serviceTypes)) {
            if (!hasPrefilledServiceTypeRef.current) {
                const defaultServiceTypes = filterServiceTypesToAvailableOptions(defaultServiceTypesByAuthTier1(activeAuth), restrictedOptions);
                if (!isEmpty(defaultServiceTypes)) {
                    setValue('serviceType', defaultServiceTypes);
                }
            }
            hasPrefilledServiceTypeRef.current = true;
            return;
        }

        hasPrefilledServiceTypeRef.current = true;

        if (isFetchingLatestHoursData) {
            return;
        }

        if (useRestrictedOptions && isEmpty(filterServiceTypesToAvailableOptions(serviceTypes, restrictedOptions))) {
            if (!isEmpty(getValues('serviceType') ?? [])) {
                setValue('serviceType', []);
            }
            return;
        }

        const clinicalHours = defaultClinicalRecommendationHoursFromAuth(activeAuth);
        const clinicalHoursByField = clinicalHours as Record<string, number | null>;
        getFieldsToResetForHourVisibility(
            getHourVisibility(serviceTypes),
            {hourRanges: true},
        ).forEach((field) => clinicalHoursByField[field] = null);
        (Object.entries(clinicalHours) as Array<[keyof typeof clinicalHours, number | null]>).forEach(([field, value]) => {
            setValue(field, value as never);
        });
    }, [showModal, activeAuth, userWantsToSubmit, reportType, serviceTypes, setValue, getValues, isFetchingLatestHoursData, restrictedOptions]);
}

type UseClientReportModalCleanupParams = {
    showModal: boolean;
    methods: UseFormReturn<ClientReportSubmitInput>;
    setSelectedAuth: (auth: AuthorizationInfo | undefined) => void;
    setUploadedFiles: (files: FilePondFile[]) => void;
    setNotifyUsers: (users: SelectOption[]) => void;
    setIsShortAddendum: (value: boolean) => void;
};

export function useClientReportModalCleanup({
    showModal,
    methods,
    setSelectedAuth,
    setUploadedFiles,
    setNotifyUsers,
    setIsShortAddendum,
}: UseClientReportModalCleanupParams) {
    const {reset} = methods;

    const clearFormData = useCallback(() => {
        reset();
        setSelectedAuth(undefined);
        setUploadedFiles([]);
        setNotifyUsers([]);
        setIsShortAddendum(false);
    }, [reset, setSelectedAuth, setUploadedFiles, setNotifyUsers, setIsShortAddendum]);

    useEffect(() => {
        if (!showModal) {
            clearFormData();
        }
    }, [showModal, clearFormData]);

    return {clearFormData};
}
